<template>
  <div class="order-select-container">
    <v-text-field
        v-model="searchText"
        :label="label"
        :variant="useGlobalSetting().globalTextVariant"
        :prepend-inner-icon="prependInnerIcon"
        :clearable="clearable"
        :rounded="rounded"
        hide-details
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        @click:clear="onClear"
        @keydown.arrow-down.prevent="navigateDown"
        @keydown.arrow-up.prevent="navigateUp"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.escape="closeDropdown"
    />

    <!-- Dropdown -->
    <div
        v-if="showDropdown && items.length > 0"
        class="order-dropdown"
        @scroll="onScroll"
    >
      <v-list density="compact" class="order-list">
        <v-list-item
            v-for="(item, index) in items"
            :key="item.id"
            :class="{ 'item-highlighted': highlightedIndex === index }"
            @mousedown.prevent="selectOrder(item)"
            @mouseenter="highlightedIndex = index"
        >
          <template #prepend>
            <v-avatar size="32" color="grey-lighten-3">
              <v-icon icon="mdi-clipboard-text-outline" size="18"/>
            </v-avatar>
          </template>

          <v-list-item-title class="font-weight-medium">
            <span v-html="highlight(`#${item.id} - ${item.title}`, searchText)"/>
          </v-list-item-title>
          <v-list-item-subtitle v-if="item.subtitle">
            {{ item.subtitle }}
          </v-list-item-subtitle>

          <template #append>
            <v-chip v-if="item.state" :color="StateColor[item.state]" size="x-small" variant="flat">
              {{ item.state }}
            </v-chip>
          </template>
        </v-list-item>

        <!-- Loading indicator -->
        <v-list-item v-if="loading" class="d-flex justify-center">
          <v-progress-circular indeterminate size="20" width="2"/>
        </v-list-item>
      </v-list>
    </div>

    <!-- No results -->
    <div v-if="showDropdown && items.length === 0 && searchText && !loading" class="order-dropdown">
      <v-list density="compact">
        <v-list-item>
          <template #prepend>
            <v-icon color="grey">mdi-magnify-close</v-icon>
          </template>
          <v-list-item-title class="text-grey">
            Keine Aufträge gefunden
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {ref, watch} from 'vue'
import {supabase} from '@/lib/supabase'
import {useGlobalSetting} from '@/lib/utils'
import {StateColor} from '@/types/Order'

interface OrderItem {
  id: number
  title: string
  subtitle: string
  state: string
}

const model = defineModel<number | null>()

const props = defineProps({
  label: {type: String, default: 'Auftrag wählen (optional)'},
  prependInnerIcon: {type: String, default: 'mdi-clipboard-text-outline'},
  clearable: {type: Boolean, default: true},
  rounded: {type: String, default: ""}
})

const searchText = ref('')
const items = ref<OrderItem[]>([])
const loading = ref(false)
const showDropdown = ref(false)
const highlightedIndex = ref(-1)
const totalItems = ref(0)
const currentOffset = ref(0)
const PAGE_SIZE = 30
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// --- Data Loading ---

async function loadPage(offset: number, search: string) {
  if (loading.value) return
  loading.value = true

  try {
    const {data, error} = await supabase.rpc('filter_rows', {
      p_table: 'orders',
      p_filters: {},
      p_sort_column: 'id',
      p_sort_order: 'desc',
      p_limit: PAGE_SIZE,
      p_offset: offset,
      p_select: 'id, title, state, work_group, asset_id',
      p_search: search?.trim() || '',
      p_search_columns: ['id', 'title']
    })

    if (error) {
      console.error('Order search failed:', error)
      return
    }

    const rows = data?.items || []
    const total = data?.total || 0

    const mapped = rows.map((o: any) => ({
      id: o.id,
      title: o.title || 'Ohne Titel',
      subtitle: [o.work_group, o.asset_id].filter(Boolean).join(' · '),
      state: o.state,
    }))

    if (offset === 0) {
      items.value = mapped
    } else {
      items.value.push(...mapped)
    }

    currentOffset.value = offset + mapped.length
    totalItems.value = total
  } finally {
    loading.value = false
  }
}

// --- Event Handlers ---

function onFocus() {
  showDropdown.value = true
  highlightedIndex.value = -1
  if (items.value.length === 0) {
    loadPage(0, searchText.value || '')
  }
}

function onBlur() {
  setTimeout(() => {
    showDropdown.value = false
    highlightedIndex.value = -1
  }, 150)
}

function onInput() {
  highlightedIndex.value = -1
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentOffset.value = 0
    loadPage(0, searchText.value || '')
  }, 300)

  // Wenn der User tippt, ist die Auswahl ungültig
  model.value = null
}

function onClear() {
  model.value = null
  searchText.value = ''
  currentOffset.value = 0
  loadPage(0, '')
}

function onScroll(event: Event) {
  const el = event.target as HTMLElement
  const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50

  if (isNearBottom && currentOffset.value < totalItems.value && !loading.value) {
    loadPage(currentOffset.value, searchText.value || '')
  }
}

function selectOrder(item: OrderItem) {
  model.value = item.id
  searchText.value = `#${item.id} - ${item.title}`
  showDropdown.value = false
  highlightedIndex.value = -1
}

function navigateDown() {
  if (highlightedIndex.value < items.value.length - 1) {
    highlightedIndex.value++
  }
}

function navigateUp() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

function selectHighlighted() {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < items.value.length) {
    selectOrder(items.value[highlightedIndex.value])
  }
}

function closeDropdown() {
  showDropdown.value = false
  highlightedIndex.value = -1
}

// --- Highlight Helper ---

function highlight(text: string, search: string | null): string {
  if (!search || !text) return text
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  return text.replace(regex, '<mark class="search-highlight">$1</mark>')
}

// --- Wenn model von außen gesetzt wird, lade den Order ---

watch(model, async (newVal) => {
  if (newVal && !searchText.value.includes(`#${newVal}`)) {
    const {data} = await supabase
        .from('orders')
        .select('id, title')
        .eq('id', newVal)
        .single()

    if (data) {
      searchText.value = `#${data.id} - ${data.title || 'Ohne Titel'}`
    }
  }
}, {immediate: true})

// Initial laden
loadPage(0, '')
</script>

<style scoped>
.order-select-container {
  position: relative;
}

.order-dropdown {
  position: absolute;
  top: calc(100% - 12px);
  left: 0;
  right: 0;
  background: rgb(var(--v-theme-surface));
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-top: none;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.order-list {
  padding: 0;
}

.order-list :deep(.v-list-item) {
  padding: 8px 16px;
  min-height: 56px;
  border-bottom: thin solid rgba(var(--v-border-color), 0.06);
  transition: background-color 0.15s ease;
}

.order-list :deep(.v-list-item:last-child) {
  border-bottom: none;
}

.order-list :deep(.v-list-item:hover),
.order-list :deep(.v-list-item.item-highlighted) {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.order-dropdown::-webkit-scrollbar {
  width: 6px;
}

.order-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.order-dropdown::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 3px;
}

.search-highlight {
  background-color: rgba(var(--v-theme-primary), 0.3);
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 600;
}
</style>
